import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import HistoryEduRoundedIcon from "@mui/icons-material/HistoryEduRounded";

import CommonPage from "../../components/common-page";
import useLanguage from "../../hooks/useLanguage";
import useToaster from "../../hooks/useToaster";
import useTodayBefore from "../../hooks/useTodayBefore";
import useUserData from "../../hooks/useUserData";
import TodayBeforeSkeleton from "./skeleton.jsx";
import {
  DatePill,
  DateText,
  EmptyOrb,
  EmptyState,
  Entry,
  EntryActions,
  EntryBody,
  EntryCard,
  EventText,
  MetaRow,
  SourceChip,
  Thumb,
  ThumbFallback,
  Timeline,
  Year,
  YearColumn,
  YearsAgo,
} from "./style.jsx";

const navigate = (to) => {
  window.history.pushState({}, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

const SOURCE_COLOR = {
  wikipedia: { bg: "rgba(70, 160, 225, 0.16)", fg: "#3B87BF" },
  knesset: { bg: "rgba(155, 107, 223, 0.16)", fg: "#7B54B8" },
};

export default function TodayBefore() {
  const [{ pages: { todaybefore: text }, direction, code }] = useLanguage();
  const { addToaster } = useToaster();
  const { listItems, refresh, generate } = useTodayBefore();
  // Refresh rebuilds the day row everyone reads, so it matches the API gate.
  const isAdmin = useUserData()?.role === "admin";

  const [day, setDay] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [inflight, setInflight] = useState(() => new Set());
  const [confirmItem, setConfirmItem] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await listItems();
        if (alive && Array.isArray(res?.items)) setDay(res);
      } catch (_) {
        /* toaster already fired in the hook */
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateLabel = useMemo(() => {
    if (!day?.dateKey) return "";
    return new Intl.DateTimeFormat(code === "heb" ? "he-IL" : "en-US", {
      day: "numeric",
      month: "long",
    }).format(new Date(`${day.dateKey}T00:00:00`));
  }, [day, code]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const res = await refresh();
      if (Array.isArray(res?.items)) {
        setDay(res);
        addToaster({ message: text.toaster.refreshed, severity: "success" });
      } else {
        addToaster({ message: text.toaster.refreshFailed, severity: "error" });
      }
    } catch (_) {
      addToaster({ message: text.toaster.refreshFailed, severity: "error" });
    } finally {
      setRefreshing(false);
    }
  };

  const handleGenerate = async (item) => {
    if (!item?.id || inflight.has(item.id)) return;
    setInflight((prev) => new Set(prev).add(item.id));
    try {
      const res = await generate(item.id);
      if (res?.enqueued || res?.alreadyQueued) {
        // Mirror the claim locally so the row reads as busy without a refetch.
        setDay((prev) =>
          prev && {
            ...prev,
            items: prev.items.map((it) =>
              it.id === item.id
                ? { ...it, generatingAt: new Date().toISOString() }
                : it
            ),
          }
        );
        addToaster(
          res.enqueued
            ? { message: text.toaster.queued, severity: "success" }
            : { message: text.toaster.alreadyQueued, severity: "info" }
        );
      } else {
        addToaster({
          message: res?.message || text.toaster.failed,
          severity: "error",
        });
      }
    } catch (e) {
      addToaster({ message: e?.message || text.toaster.failed, severity: "error" });
    } finally {
      setInflight((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const DateEl = (
    <DatePill
      key="date"
      onClick={isAdmin ? handleRefresh : undefined}
      $disabled={refreshing || !isAdmin}
      role={isAdmin ? "button" : undefined}
      aria-label={isAdmin ? text.refresh : undefined}
      title={isAdmin ? text.refresh : undefined}
    >
      <DateText>{refreshing ? text.refreshing : dateLabel}</DateText>
      {isAdmin && (
        <RefreshRoundedIcon
          fontSize="small"
          sx={{
            fontSize: 14,
            color: "text.secondary",
            transition: "transform 0.4s ease",
            transform: refreshing ? "rotate(180deg)" : "none",
          }}
        />
      )}
    </DatePill>
  );

  const items = day?.items || [];
  const thisYear = Number(day?.dateKey?.slice(0, 4)) || new Date().getFullYear();

  return (
    <CommonPage
      title={text.title}
      subtitle={text.subtitle}
      actions={[DateEl]}
    >
      {!loaded ? (
        <TodayBeforeSkeleton />
      ) : items.length === 0 ? (
        <EmptyState>
          <EmptyOrb>
            <HistoryEduRoundedIcon sx={{ fontSize: 36 }} />
          </EmptyOrb>
          <Typography variant="h6" fontWeight={700}>
            {text.empty}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {text.emptyHint}
          </Typography>
        </EmptyState>
      ) : (
        <Timeline>
          {items.map((item, i) => {
            const done = !!item.articleId;
            const busy = !done && (!!item.generatingAt || inflight.has(item.id));
            const color = SOURCE_COLOR[item.source] || SOURCE_COLOR.wikipedia;
            return (
              <Entry key={item.id} style={{ "--i": i }}>
                <YearColumn>
                  <Year>{item.year}</Year>
                  <YearsAgo>
                    {text.yearsAgo.replace("{n}", thisYear - item.year)}
                  </YearsAgo>
                </YearColumn>

                <EntryCard $done={done}>
                  {item.imageUrl ? (
                    <Thumb
                      src={item.imageUrl}
                      alt=""
                      loading="lazy"
                      $muted={done}
                    />
                  ) : (
                    <ThumbFallback $muted={done}>
                      <HistoryEduRoundedIcon />
                    </ThumbFallback>
                  )}

                  <EntryBody>
                    <EventText $muted={done}>{item.hebrewText}</EventText>
                    <MetaRow>
                      <SourceChip
                        label={text.sources[item.source] || item.source}
                        $bg={color.bg}
                        $fg={color.fg}
                      />
                      {done && (
                        <Typography variant="caption" color="success.main" fontWeight={700}>
                          {text.state.generated}
                        </Typography>
                      )}
                      {busy && (
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          {text.state.generating}
                        </Typography>
                      )}
                    </MetaRow>
                  </EntryBody>

                  <EntryActions>
                    <Tooltip title={text.openSource}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          window.open(item.sourceUrl, "_blank", "noopener,noreferrer")
                        }
                      >
                        <OpenInNewRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {done ? (
                      <Tooltip title={text.openArticle}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/editor/${item.articleId}`)}
                          sx={{
                            border: 1,
                            borderColor: "success.main",
                            color: "success.main",
                          }}
                        >
                          <CheckRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : busy ? (
                      <Tooltip title={text.state.generating}>
                        <CircularProgress size={20} sx={{ mx: 0.75 }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title={text.generate}>
                        <IconButton
                          size="small"
                          onClick={() => setConfirmItem(item)}
                          sx={{
                            bgcolor: "text.primary",
                            color: "background.default",
                            "&:hover": { bgcolor: "text.secondary" },
                          }}
                        >
                          <AutoAwesomeRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </EntryActions>
                </EntryCard>
              </Entry>
            );
          })}
        </Timeline>
      )}

      <Dialog
        open={!!confirmItem}
        onClose={() => setConfirmItem(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { direction } }}
      >
        <DialogTitle>{text.confirm.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {text.confirm.message}
          </DialogContentText>
          <DialogContentText
            dir="rtl"
            sx={{ fontWeight: 700, color: "text.primary", textAlign: "start" }}
          >
            {confirmItem?.hebrewText}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmItem(null)} color="inherit">
            {text.confirm.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const item = confirmItem;
              setConfirmItem(null);
              handleGenerate(item);
            }}
            sx={{
              bgcolor: "text.primary",
              color: "background.default",
              "&:hover": { bgcolor: "text.secondary" },
            }}
          >
            {text.confirm.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </CommonPage>
  );
}
